import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar, User, Search, ArrowRight, Loader2, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getFullUrl } from '../utils/url';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Toutes');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedArticle, setExpandedArticle] = useState(null);

  const categories = ['Toutes', 'Réglementation', 'Fiscalité', 'Digital', 'Conseil'];

  // Charger les articles depuis l'API
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/articles`);
        setArticles(response.data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Erreur lors du chargement des articles:', err);
        }
        setError('Impossible de charger les articles. Veuillez réessayer plus tard.');
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.resume.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'Toutes' || article.categorie === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Actualités Comptables & Conseils Experts | Blog MMD CONSEIL Lyon"
        description="Suivez les dernières actualités comptables et fiscales sur notre blog. Conseils d'experts, évolutions réglementaires et astuces pour optimiser votre gestion d'entreprise."
        keywords="actualités comptables Lyon, blog expert-comptable, conseils fiscaux, réglementation comptable, optimisation fiscale PME, nouvelles lois comptables"
        url={getFullUrl("/actualites")}
      />
      
      {/* Hero Section */}
      <section className="hero-gradient py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-white text-primary-blue px-4 py-2">
              Actualités & Conseils
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-text-dark mb-6">
              Restez Informé des Dernières Évolutions
            </h1>
            <p className="text-xl text-text-gray leading-relaxed">
              Suivez la veille comptable et fiscale mise à jour chaque jour à partir des sources officielles 
              et de la presse spécialisée, en complément de nos articles d'expertise MMD CONSEIL.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-gray" />
                <Input
                  type="text"
                  placeholder="Rechercher un article..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 form-input"
                />
              </div>

              {/* Categories */}
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(category)}
                    className={selectedCategory === category ? 'btn-primary' : 'btn-secondary'}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="section-spacing bg-white">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary-blue mr-3" />
              <span className="text-text-gray">Chargement des articles...</span>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-red-500 text-2xl">⚠️</span>
              </div>
              <h3 className="text-2xl font-semibold text-text-dark mb-4">
                Erreur de chargement
              </h3>
              <p className="text-text-gray mb-6">{error}</p>
              <Button
                onClick={() => window.location.reload()}
                className="btn-primary px-6 py-2"
              >
                Réessayer
              </Button>
            </div>
          ) : filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 gap-8">
              {filteredArticles.map((article) => (
                <Card key={article.id} className="service-card bg-white hover:bg-light-gray" data-testid={`article-card-${article.id}`}>
                  <CardContent className="p-6">
                    <div className="mb-4">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <Badge className="bg-light-blue text-primary-blue">
                          {article.categorie}
                        </Badge>
                        {article.source && article.lien && (
                          <Badge variant="outline" className="border-accent-blue text-accent-blue" data-testid={`article-source-${article.id}`}>
                            Source : {article.source}
                          </Badge>
                        )}
                      </div>
                      <h3 className="text-2xl font-semibold text-text-dark mb-3 leading-tight">
                        {article.titre}
                      </h3>
                      <p className="text-text-gray leading-relaxed mb-4">
                        {article.resume}
                      </p>
                      
                      {/* Contenu complet - affiché si l'article est expansé (articles MMD uniquement) */}
                      {!article.lien && expandedArticle === article.id && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          <div 
                            className="text-text-dark leading-relaxed prose prose-lg max-w-none"
                            style={{
                              whiteSpace: 'pre-line',
                              lineHeight: '1.8'
                            }}
                          >
                            {article.contenu}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-sm text-text-gray border-t pt-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(article.datePublication)}</span>
                        </div>
                      </div>
                      {article.lien ? (
                        <a
                          href={article.lien}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-primary-blue hover:text-accent-blue font-medium px-4 py-2"
                          data-testid={`article-source-link-${article.id}`}
                        >
                          Lire sur le site source
                          <ExternalLink className="w-4 h-4 ml-2" />
                        </a>
                      ) : (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-primary-blue hover:text-accent-blue px-4 py-2"
                          data-testid={`article-expand-${article.id}`}
                          onClick={() => setExpandedArticle(
                            expandedArticle === article.id ? null : article.id
                          )}
                        >
                          {expandedArticle === article.id ? (
                            <>
                              Masquer l'article
                              <ArrowRight className="w-4 h-4 ml-2 rotate-90" />
                            </>
                          ) : (
                            <>
                              Lire l'article complet
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-light-gray rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-12 h-12 text-text-gray" />
              </div>
              <h3 className="text-2xl font-semibold text-text-dark mb-4">
                Aucun article trouvé
              </h3>
              <p className="text-text-gray mb-6">
                Essayez de modifier vos critères de recherche ou explorez toutes nos catégories.
              </p>
              <Button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('Toutes');
                }}
                className="btn-primary px-6 py-2"
              >
                Voir tous les articles
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="section-spacing bg-light-gray">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-text-dark mb-4">
              Restez Connecté à l'Actualité
            </h2>
            <p className="text-xl text-text-gray mb-8">
              Recevez nos derniers articles et conseils directement dans votre boîte email
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Votre adresse email"
                className="flex-1 form-input"
              />
              <Button className="btn-primary px-6 py-2 whitespace-nowrap">
                S'abonner
              </Button>
            </div>
            
            <p className="text-sm text-text-gray mt-4">
              Nous respectons votre vie privée. Désabonnement possible à tout moment.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-primary-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Besoin de Conseils Personnalisés ?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Nos experts sont là pour répondre à vos questions spécifiques et vous accompagner
          </p>
          <Button asChild size="lg" className="bg-white text-primary-blue hover:bg-blue-50 px-8 py-4 text-lg rounded-xl">
            <Link to="/contact">
              Prendre rendez-vous
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Blog;